import { useTranslation } from 'react-i18next';

import { Tag } from '@natter-co/design-system';
import { blue, orange } from 'common/theme/palette';
import { isCommunityAdmin, VISUAL_REFRESH } from 'common/utils';
import { UserRoleName } from 'domain/UserProfile';

import { Badge } from '../Badge';

import type { TagColor } from '@natter-co/design-system';

type Member = {
  accountId?: number;
  category?: string;
  communityRole?: UserRoleName;
  role?: UserRoleName | string | null;
};

type Props = {
  member: Member;
  communityOwnerId: number | undefined;
};

/**
 * Under the visual refresh a role is a sentence-case DS `Tag`; the legacy `Badge` is uppercase, so
 * it keeps its literal copy. The colours carry across: the owner's blue is `primary`, the admin's
 * orange is `warning`.
 */
const ROLES = {
  communityOwner: { tagColor: 'primary', badgeColor: blue[600], legacyLabel: 'COMMUNITY OWNER' },
  admin: { tagColor: 'warning', badgeColor: orange[300], legacyLabel: 'ADMIN' },
} satisfies Record<string, { tagColor: TagColor; badgeColor: string; legacyLabel: string }>;

export const MemberRoleLabel = ({ member, communityOwnerId }: Props) => {
  const { t } = useTranslation('common', { keyPrefix: 'memberRoleLabel' });
  // Both sides can be missing — the organisation members list passes no owner id and its members
  // carry no `accountId` — and `undefined === undefined` would label every row as the owner.
  const isOwner = communityOwnerId !== undefined && communityOwnerId === member.accountId;
  const roleKey = isOwner ? 'communityOwner' : isCommunityAdmin(member) ? 'admin' : null;

  if (!roleKey) {
    return null;
  }

  const role = ROLES[roleKey];

  return VISUAL_REFRESH ? (
    <Tag color={role.tagColor} size="sm">
      {t(roleKey)}
    </Tag>
  ) : (
    <Badge backgroundColor={role.badgeColor}>{role.legacyLabel}</Badge>
  );
};
